import { ChevronLeft } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Separator } from "@/components/ui/separator";
import { Skeleton } from "@/components/ui/skeleton";
import { useImportJob } from "@/api/import";
import { useVisibleT } from "@/lib/cms/cms-new-i18n";
import {
  IMPORT_STEPS,
  ImportAnalysisReport,
  ImportStep,
  ImportTable,
  isImportableTable,
} from "@/lib/import/import-types";
import { useTranslation } from "react-i18next";
import { tabMenuKeyToTranslationKey } from "@/lib/utils";
import { ImportStepper } from "./components/ImportStepper";
import { ImportStepTable } from "./steps/ImportStepTable";
import { ImportHistory } from "./components/ImportHistory";
import { ImportStepTemplate } from "./steps/ImportStepTemplate";
import { ImportStepUpload } from "./steps/ImportStepUpload";
import { ImportStepReview } from "./steps/ImportStepReview";
import { ImportStepRelations } from "./steps/ImportStepRelations";
import { ImportStepConfirm } from "./steps/ImportStepConfirm";
import { ImportStepRunning } from "./steps/ImportStepRunning";
import { ImportStepReport } from "./steps/ImportStepReport";

/**
 * Wizard d'import.
 *
 * ## Deux choix d'architecture
 *
 * **L'etape, la table et le job vivent dans l'URL**
 * (`?table=artwork&job=…&step=relations`) : resistant au rafraichissement,
 * partageable avec le support, et le bouton retour du navigateur fonctionne.
 *
 * **Toutes les decisions vivent cote serveur.** Le rapport d'analyse est
 * remis en cache local pour l'affichage, mais l'etat qui compte — correspondances,
 * arbitrages, progression — est en base. Un rafraichissement en pleine
 * arbitration ne perd donc rien : c'est ce qui rend le `?job=` suffisant pour
 * reprendre.
 */
const isStep = (value: string | null): value is ImportStep =>
  value != null && (IMPORT_STEPS as readonly string[]).includes(value);

// Seules les tables ouvertes à l'import sont acceptées depuis l'URL : un
// `?table=author` (lancé depuis un autre dashboard) est ignoré et ramène à
// l'étape « Que voulez-vous importer ? » au lieu de planter.
const isTable = isImportableTable;

export const ImportWizard = () => {
  const vt = useVisibleT();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();

  const table = isTable(searchParams.get("table"))
    ? (searchParams.get("table") as ImportTable)
    : null;
  const jobId = searchParams.get("job") ?? null;

  const [report, setReport] = useState<ImportAnalysisReport | null>(null);
  const [pendingCount, setPendingCount] = useState(0);
  const headingRef = useRef<HTMLHeadingElement>(null);

  const requested = isStep(searchParams.get("step"))
    ? (searchParams.get("step") as ImportStep)
    : "table";

  /**
   * Etapes qui ont besoin du rapport d'analyse cote client.
   *
   * Le rapport n'est pas persiste dans le navigateur : sur un lien profond (ex.
   * clic depuis l'historique) ou un rafraichissement, on le **re-hydrate depuis
   * le serveur** (`getJob` renvoie `report`), ce qui permet de reprendre un
   * import a la bonne etape — mapping, liaisons ou recapitulatif — et pas
   * seulement le rapport final.
   */
  const REPORT_STEPS: ImportStep[] = ["review", "relations", "confirm"];
  const needsReport = REPORT_STEPS.includes(requested);

  const { data: hydrationJob, isLoading: hydrating } = useImportJob(
    needsReport && !report ? jobId ?? undefined : undefined,
  );

  useEffect(() => {
    if (jobId && report === null && needsReport && hydrationJob?.report) {
      setReport(hydrationJob.report);
      setPendingCount(hydrationJob.pendingDecisionCount ?? 0);
    }
  }, [jobId, report, needsReport, hydrationJob]);

  /**
   * Etape effective, **bornee** a ce que l'etat autorise : une etape dependant
   * d'un job exige un `job=` dans l'URL. Les etapes a rapport (review/relations/
   * confirm) s'appuient sur le rapport re-hydrate ci-dessus.
   */
  let step: ImportStep = requested;
  if (!table) step = "table";
  else if (
    (requested === "relations" ||
      requested === "confirm" ||
      requested === "running" ||
      requested === "report" ||
      requested === "review") &&
    !jobId
  ) {
    step = "upload";
  }

  const goTo = (next: ImportStep, extra?: Record<string, string>) => {
    const params = new URLSearchParams(searchParams);
    params.set("step", next);
    for (const [key, value] of Object.entries(extra ?? {})) params.set(key, value);
    setSearchParams(params, { replace: false });
  };

  const chooseTable = (next: ImportTable) => {
    const params = new URLSearchParams(searchParams);
    params.set("table", next);
    params.set("step", "table");
    params.delete("job");
    setSearchParams(params, { replace: true });
    setReport(null);
    setPendingCount(0);
  };

  const restart = () => {
    const params = new URLSearchParams(searchParams);
    params.delete("job");
    params.set("step", "template");
    setSearchParams(params, { replace: false });
    setReport(null);
    setPendingCount(0);
  };

  // Huit etapes sans gestion du focus seraient inutilisables au clavier.
  useEffect(() => {
    headingRef.current?.focus();
  }, [step]);

  const titles: Record<ImportStep, string> = {
    table: vt("bo.import.step.table", "Que voulez-vous importer ?"),
    template: vt("bo.import.step.template", "Téléchargez le modèle"),
    upload: vt("bo.import.step.upload", "Déposez votre fichier"),
    review: vt("bo.import.step.review", "Vérifions vos données"),
    relations: vt("bo.import.step.relations", "Confirmons les liaisons"),
    confirm: vt("bo.import.step.confirm", "Récapitulatif avant import"),
    running: vt("bo.import.step.running", "Import en cours"),
    report: vt("bo.import.step.report", "Rapport"),
  };

  // Affiche pendant la re-hydratation du rapport (lien profond / refresh) ;
  // si le job n'a finalement pas de rapport exploitable, propose le rapport final.
  const resuming = (
    <div className="flex flex-col gap-3">
      <Skeleton className="h-8 w-1/3" />
      <Skeleton className="h-24 w-full" />
      {!hydrating && !hydrationJob?.report && (
        <p className="text-sm text-gray-600">
          {vt("bo.import.resume.unavailable", "Impossible de reprendre cet import ici.")}{" "}
          <button
            type="button"
            className="underline"
            onClick={() => goTo("report")}
          >
            {vt("bo.import.resume.seeReport", "Voir le rapport")}
          </button>
        </p>
      )}
    </div>
  );

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        {table && (
          <Link
            to={`/dashboard/${table}`}
            className="flex items-center gap-1 text-sm text-gray-600 hover:text-gray-900 w-fit"
          >
            <ChevronLeft className="w-4 h-4" aria-hidden />
            {t("goBack")}
          </Link>
        )}

        <div className="flex flex-col gap-1">
          <h1
            ref={headingRef}
            tabIndex={-1}
            className="text-2xl font-semibold outline-none"
          >
            {vt("bo.import.title", "Importer un fichier Excel")}
            {table && (
              <span className="text-gray-500 font-normal">
                {" — "}
                {t(tabMenuKeyToTranslationKey(table))}
              </span>
            )}
          </h1>
          <p className="text-sm text-gray-600">{titles[step]}</p>
        </div>

        <ImportStepper current={step} onGoTo={(target) => goTo(target)} />
      </div>

      <Separator />

      {step === "table" && (
        <div className="flex flex-col gap-8">
          <ImportStepTable
            table={table}
            onTableChange={chooseTable}
            onNextStep={() => goTo("template")}
          />
          <ImportHistory />
        </div>
      )}

      {step === "template" && table && (
        <ImportStepTemplate
          table={table}
          onNextStep={() => goTo("upload")}
          onBack={() => goTo("table")}
        />
      )}

      {step === "upload" && table && (
        <ImportStepUpload
          table={table}
          onAnalysed={(result) => {
            setReport(result.report);
            setPendingCount(result.pendingDecisionCount);
            goTo("review", { job: result.jobId });
          }}
          onBack={() => goTo("template")}
        />
      )}

      {step === "review" &&
        (report ? (
          <ImportStepReview
            report={report}
            jobId={jobId}
            table={table as ImportTable}
            pendingCount={pendingCount}
            onReportChange={setReport}
            onContinue={() => goTo("relations")}
            onBack={() => {
              setReport(null);
              goTo("upload");
            }}
          />
        ) : (
          resuming
        ))}

      {step === "relations" &&
        jobId &&
        (report ? (
          <ImportStepRelations
            jobId={jobId}
            report={report}
            onNextStep={() => goTo("confirm")}
            onBack={() => goTo("review")}
          />
        ) : (
          resuming
        ))}

      {step === "confirm" &&
        jobId &&
        table &&
        (report ? (
          <ImportStepConfirm
            jobId={jobId}
            report={report}
            onStarted={() => goTo("running")}
            onBack={() => goTo("relations")}
          />
        ) : (
          resuming
        ))}

      {step === "running" && jobId && table && (
        <ImportStepRunning
          jobId={jobId}
          table={table}
          onFinished={() => goTo("report")}
        />
      )}

      {step === "report" && jobId && table && (
        <ImportStepReport jobId={jobId} table={table} onRestart={restart} />
      )}
    </div>
  );
};
